import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ChevronLeft, Save, Wallet, Info } from "lucide-react";

/**
 * PayoutSettingsPage
 * ──────────────────
 * Verkäufer wählt seine Auszahlungsmethode:
 *   - PAYPAL: E-Mail-Adresse
 *   - BANK:   IBAN + BIC + Kontoinhaber
 *
 * Zusätzlich: Historie der letzten Payouts.
 */
export default function PayoutSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [method, setMethod] = useState("PAYPAL");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankBic, setBankBic] = useState("");
  const [bankHolder, setBankHolder] = useState("");
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const flash = (type, text, ms = 3500) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), ms);
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: settings }, { data: hist }] = await Promise.all([
          api.get("/profile/payout-settings"),
          api.get("/profile/payouts"),
        ]);
        if (settings.method) setMethod(settings.method);
        setPaypalEmail(settings.paypal_email || "");
        setBankIban(settings.bank_iban || "");
        setBankBic(settings.bank_bic || "");
        setBankHolder(settings.bank_holder || "");
        setPayouts(hist.payouts || []);
      } catch (e) {
        flash("error", formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const payload = { method };
    if (method === "PAYPAL") {
      payload.paypal_email = paypalEmail.trim().toLowerCase();
    } else {
      payload.bank_iban = bankIban.trim().toUpperCase().replace(/\s+/g, "");
      payload.bank_bic = bankBic.trim().toUpperCase().replace(/\s+/g, "");
      payload.bank_holder = bankHolder.trim();
    }
    try {
      await api.put("/profile/payout-settings", payload);
      flash("success", "Auszahlungsmethode gespeichert");
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (user === null) return null;  // Auth wird noch geladen
  if (user === false) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center">
          <p className="text-sm text-[#666]">{t("payout.please_login")}</p>
          <button onClick={() => navigate("/login")} className="mt-3 rounded-full h-10 px-4 bg-[#0A0A0A] text-white text-sm">{t("payout.login")}</button>
        </div>
      </div>
    );
  }

  const statusBadge = {
    PAID_OUT: "bg-green-50 text-green-700 border-green-200",
    PENDING_MANUAL: "bg-amber-50 text-amber-700 border-amber-200",
    PAYING_OUT: "bg-blue-50 text-blue-700 border-blue-200",
    FAILED: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="max-w-md mx-auto">
        <div className="sticky top-0 z-30 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="h-8 w-8 flex items-center justify-center" data-testid="payout-back">
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-base font-bold text-[#0A0A0A] flex items-center gap-2">
            <Wallet size={16} /> {t("payout.title")}
          </h1>
        </div>

        <div className="px-4 py-5 space-y-5">
          {msg.text && (
            <div className={`px-3 py-2 border text-sm ${msg.type === "success" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
              {msg.text}
            </div>
          )}

          <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs text-[#666] flex gap-2">
            <Info size={14} className="flex-shrink-0 mt-0.5" />
            <p>{t("payout.info")}</p>
          </div>

          {loading ? (
            <div className="text-center py-8 text-sm text-[#999]">{t("common.loading")}</div>
          ) : (
            <>
              {/* Methode-Wahl */}
              <div>
                <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-2">{t("payout.choose_method")}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setMethod("PAYPAL")}
                    data-testid="payout-method-paypal"
                    className={`h-14 border text-sm font-medium transition-colors ${method === "PAYPAL" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#666] hover:border-[#0A0A0A]"}`}>
                    {t("payout.paypal")}
                  </button>
                  <button onClick={() => setMethod("BANK")}
                    data-testid="payout-method-bank"
                    className={`h-14 border text-sm font-medium transition-colors ${method === "BANK" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#666] hover:border-[#0A0A0A]"}`}>
                    {t("payout.bank_transfer")}
                  </button>
                </div>
              </div>

              {/* Method Details */}
              {method === "PAYPAL" ? (
                <div>
                  <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider">{t("payout.paypal_email")}</label>
                  <input type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)}
                    data-testid="payout-paypal-email"
                    placeholder={t("payout_extra.paypal_placeholder")}
                    className="mt-1 w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
                  <p className="text-[10px] text-[#999] mt-1">{t("payout.paypal_email_hint")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider">{t("payout.account_holder")}</label>
                    <input value={bankHolder} onChange={(e) => setBankHolder(e.target.value)}
                      data-testid="payout-bank-holder"
                      placeholder={t("payout.account_holder_placeholder")}
                      className="mt-1 w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider">{t("payout.iban")}</label>
                    <input value={bankIban} onChange={(e) => setBankIban(e.target.value.toUpperCase())}
                      data-testid="payout-bank-iban"
                      placeholder="DE00 0000 0000 0000 0000 00"
                      className="mt-1 w-full h-11 px-3 border border-[#E5E5E5] text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider">{t("payout.bic_optional")}</label>
                    <input value={bankBic} onChange={(e) => setBankBic(e.target.value.toUpperCase())}
                      data-testid="payout-bank-bic"
                      placeholder="COBADEFFXXX"
                      className="mt-1 w-full h-11 px-3 border border-[#E5E5E5] text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
                  </div>
                </div>
              )}

              <button onClick={save} disabled={saving}
                data-testid="payout-save"
                className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                <Save size={14} /> {saving ? t("payout.saving") : t("payout.save")}
              </button>
            </>
          )}

          {/* History */}
          {payouts.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-2 mt-6">{t("payout.recent_payouts")}</p>
              <div className="bg-white border border-[#E5E5E5]">
                {payouts.slice(0, 20).map(p => (
                  <div key={p.payout_id}
                    data-testid={`payout-history-${p.payout_id}`}
                    className="flex items-center justify-between px-3 py-2.5 border-b border-[#F0F0F0] last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold border rounded ${statusBadge[p.status] || "bg-gray-100 border-gray-200 text-gray-700"}`}>
                          {p.status}
                        </span>
                        <span className="text-xs text-[#666]">{p.method}</span>
                      </div>
                      <p className="text-[10px] text-[#999] mt-0.5">
                        {p.created_at?.slice(0, 16).replace("T", " ")} · {t("payout.orders_count", { count: p.order_count })}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-[#0A0A0A] font-mono">
                      {Number(p.amount).toFixed(2)} {p.currency}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
